import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Animated, Easing, View } from 'react-native';

import { fullWidth, scale } from '@components/frame';

import { CLOSE_DURATION_MS, MENU_OFFSET_Y, OPEN_DURATION_MS } from '@components/common/dropdown/constants';
import type { DropdownProps } from '@components/common/dropdown/types';

export type UseManageDropdownParams<T extends string = string> = Pick<DropdownProps<T>, 'items' | 'selectedValue' | 'onChange'>;

export const useManageDropdown = <T extends string = string>({ items, selectedValue, onChange }: UseManageDropdownParams<T>) => {
  const triggerRef = useRef<View>(null);
  const [open, setOpen] = useState(false);
  const [shouldRenderDropdownMenu, setShouldRenderDropdownMenu] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, right: 0, width: 0 });
  const animProgress = useRef(new Animated.Value(0)).current;

  const selectedLabel = useMemo(
    () => items.find((i) => i.value === selectedValue)?.label ?? '',
    [items, selectedValue],
  );

  const handleOpen = useCallback(() => {
    triggerRef.current?.measureInWindow((x, y, width, height) => {
      setPosition({
        top: y + height + scale(MENU_OFFSET_Y),
        left: x,
        right: fullWidth - x - width,
        width,
      });
      setShouldRenderDropdownMenu(true);
      setOpen(true);
    });
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleSelect = useCallback(
    (value: T) => () => {
      onChange(value);
      setOpen(false);
    },
    [onChange],
  );

  useEffect(() => {
    if (!shouldRenderDropdownMenu) return;
    Animated.timing(animProgress, {
      toValue: open ? 1 : 0,
      duration: open ? OPEN_DURATION_MS : CLOSE_DURATION_MS,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start(({ finished }) => {
      if (!finished) return;
      // Unmount the menu only after the close animation actually completes — otherwise
      // re-opening mid-close starts a new animation against the stale value.
      if (!open) setShouldRenderDropdownMenu(false);
    });
  }, [open, shouldRenderDropdownMenu, animProgress]);

  return {
    triggerRef,
    open,
    shouldRenderDropdownMenu,
    position,
    selectedLabel,
    animProgress,
    handleOpen,
    handleClose,
    handleSelect,
  };
};
